import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
/* import axios from 'axios'; // Removed as we use fetch now */
import { ZoneNode } from '../utils/zoneUtils';

interface GeoDataContextType {
    zoneDefinitions: ZoneNode[] | null;
    polygons: any | null;
    isLoadingZones: boolean;
    isLoadingPolygons: boolean;
    errorZones: string | null;
    errorPolygons: string | null;
    loadZones: () => Promise<void>;
    loadPolygons: () => Promise<void>;
}

const GeoDataContext = createContext<GeoDataContextType>({
    zoneDefinitions: null,
    polygons: null,
    isLoadingZones: false,
    isLoadingPolygons: false,
    errorZones: null,
    errorPolygons: null,
    loadZones: async () => {},
    loadPolygons: async () => {},
});

export const useGeoData = () => useContext(GeoDataContext);

export const GeoDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [zoneDefinitions, setZoneDefinitions] = useState<ZoneNode[] | null>(null);
    const [polygons, setPolygons] = useState<any | null>(null);

    const [isLoadingZones, setIsLoadingZones] = useState(false);
    const [isLoadingPolygons, setIsLoadingPolygons] = useState(false);

    const [errorZones, setErrorZones] = useState<string | null>(null);
    const [errorPolygons, setErrorPolygons] = useState<string | null>(null);

    // Use refs to prevent simultaneous duplicate requests
    const zoneRequestRef = useRef<Promise<void> | null>(null);
    const polygonRequestRef = useRef<Promise<void> | null>(null);

    const loadZones = useCallback(async () => {
        if (zoneDefinitions) return; // Already loaded
        if (zoneRequestRef.current) return zoneRequestRef.current; // Request in flight

        setIsLoadingZones(true);
        setErrorZones(null);

        zoneRequestRef.current = fetch('/assets/zone_definitions.json')
            .then(res => {
                if (!res.ok) throw new Error('Failed to load zone definitions');
                return res.json();
            })
            .then(data => {
                setZoneDefinitions(data);
                setIsLoadingZones(false);
                zoneRequestRef.current = null;
            })
            .catch(err => {
                console.error("Failed to load zone definitions", err);
                setErrorZones('Konnte Orte nicht laden.');
                setIsLoadingZones(false);
                zoneRequestRef.current = null;
            });

        return zoneRequestRef.current;
    }, [zoneDefinitions]);

    const loadPolygons = useCallback(async () => {
        if (polygons) return; // Already loaded
        if (polygonRequestRef.current) return polygonRequestRef.current; // Request in flight

        setIsLoadingPolygons(true);
        setErrorPolygons(null);

        polygonRequestRef.current = fetch('/assets/zip_polygons_simplified.json')
            .then(res => {
                if (!res.ok) throw new Error('Failed to load map data');
                return res.json();
            })
            .then(data => {
                setPolygons(data);
                setIsLoadingPolygons(false);
                polygonRequestRef.current = null;
            })
            .catch(err => {
                console.error('Map data load error:', err);
                setErrorPolygons('Kartendaten konnten nicht geladen werden.');
                setIsLoadingPolygons(false);
                polygonRequestRef.current = null;
            });

        return polygonRequestRef.current;
    }, [polygons]);

    return (
        <GeoDataContext.Provider value={{
            zoneDefinitions,
            polygons,
            isLoadingZones,
            isLoadingPolygons,
            errorZones,
            errorPolygons,
            loadZones,
            loadPolygons,
        }}>
            {children}
        </GeoDataContext.Provider>
    );
};
